import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { ChartSeries } from "./SeriesBarChart";

export interface LineSeries extends ChartSeries {
  /**
   * Which Y axis the series reads against. Two quantities in different units
   * — money and a count — share the X axis and nothing else, so each gets a
   * scale of its own: left for the first kind, right for the second.
   */
  axis?: "left" | "right";
}

export interface SeriesLineChartProps {
  data: Array<Record<string, unknown>>;
  xKey: string;
  series: LineSeries[];
  height?: number;
  /** Formats the left axis and its series' tooltip values. */
  formatLeft?: (value: number) => string;
  /** Formats the right axis and its series' tooltip values; omitted when nothing reads it. */
  formatRight?: (value: number) => string;
}

const DEFAULT_COLORS = ["var(--chart-1)", "var(--chart-2)", "var(--chart-3)", "var(--chart-4)", "var(--chart-5)", "var(--chart-6)"];

/**
 * A line chart wired to the design tokens — the `SeriesBarChart` for series
 * over time, with an optional second axis.
 *
 * Same reason to exist: axes, grid, tooltip and legend are styled once here
 * against the token palette, so every chart in the suite reads in both themes.
 * Lines carry a dot per point, because a month with no sales is a real point
 * on the floor and not a gap in a curve.
 */
export function SeriesLineChart({
  data,
  xKey,
  series,
  height = 240,
  formatLeft = (value) => new Intl.NumberFormat().format(value),
  formatRight,
}: SeriesLineChartProps) {
  const usesRight = series.some((entry) => entry.axis === "right");
  const formatterFor = (name: string) => {
    const entry = series.find((candidate) => candidate.label === name);
    return entry?.axis === "right" && formatRight ? formatRight : formatLeft;
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 8, right: usesRight ? 4 : 8, bottom: 0, left: 4 }}>
        <CartesianGrid stroke="var(--line-soft)" vertical={false} />
        <XAxis
          dataKey={xKey}
          stroke="var(--ink-faint)"
          tick={{ fill: "var(--ink-muted)", fontSize: 12 }}
          tickLine={false}
          axisLine={{ stroke: "var(--line)" }}
        />
        <YAxis
          yAxisId="left"
          stroke="var(--ink-faint)"
          tick={{ fill: "var(--ink-muted)", fontSize: 11 }}
          tickLine={false}
          axisLine={false}
          tickFormatter={formatLeft}
          width={64}
        />
        {usesRight && (
          <YAxis
            yAxisId="right"
            orientation="right"
            stroke="var(--ink-faint)"
            tick={{ fill: "var(--ink-muted)", fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={formatRight ?? formatLeft}
            width={48}
          />
        )}
        <Tooltip
          cursor={{ stroke: "var(--line)" }}
          formatter={(value: number | string, name: string) =>
            typeof value === "number" ? formatterFor(name)(value) : value
          }
          contentStyle={{
            background: "var(--surface-over)",
            border: "1px solid var(--line)",
            borderRadius: 6,
            color: "var(--ink)",
            fontSize: 13,
          }}
          labelStyle={{ color: "var(--ink-muted)" }}
        />
        <Legend wrapperStyle={{ fontSize: 13, color: "var(--ink-muted)" }} iconType="circle" iconSize={9} />
        {series.map((entry, index) => (
          <Line
            key={entry.key}
            yAxisId={entry.axis ?? "left"}
            type="monotone"
            dataKey={entry.key}
            name={entry.label}
            stroke={entry.color ?? DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
            strokeWidth={2}
            dot={{ r: 3, strokeWidth: 0, fill: entry.color ?? DEFAULT_COLORS[index % DEFAULT_COLORS.length] }}
            activeDot={{ r: 5 }}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}
